import React, { useMemo, useState } from 'react';

import { Link } from '~/lib/react-router-dom';

import { ContactSupportOutlined, Inventory2Outlined, ListOutlined, ReceiptLongOutlined } from '@mui/icons-material';
import { Box, IconButton } from '@mui/material';
import { GridColDef, GridRowParams } from '@mui/x-data-grid';

import { getCurrencySymbolIntl } from '~/components/Refund/RefundV2/Utils/RefundUtils';
import Accordion from '~/components/v2/common/Accordion/Accordion';
import ReadOnlyField from '~/components/v2/common/ReadOnlyField/ReadOnlyField';
import Table from '~/components/v2/common/Table/Table';

import OpportunityDealInclusionsModal from './OpportunityDealInclusionsModal';

const columns: Array<GridColDef<App.Deal>> = [
  {
    field: 'name',
    headerName: 'Deal Option Name',
    flex: 3,
    renderCell: (params) => (
      <Box sx={{ whiteSpace: 'normal', lineHeight: '1.5', paddingLeft: 1 }}>{params.value || '-'}</Box>
    ),
  },
  {
    field: 'allocation__c',
    headerName: 'Allocation/day',
    flex: 3,
    renderCell: (params) => (
      <Box sx={{ whiteSpace: 'normal', lineHeight: '1.5', paddingLeft: 1 }}>{params.value || '-'}</Box>
    ),
  },
  {
    field: 'cost_price__c',
    headerName: 'Cost Price',
    flex: 1,
    renderCell: (params) => (
      <Box sx={{ whiteSpace: 'normal', lineHeight: '1.5', paddingLeft: 1 }}>
        {params.value
          ? (params.row.currencyisocode ? getCurrencySymbolIntl(params.row.currencyisocode) : '') + params.value
          : '-'}
      </Box>
    ),
  },
  {
    field: 'price__c',
    headerName: 'Sell Price',
    flex: 1,
    renderCell: (params) => (
      <Box sx={{ whiteSpace: 'normal', lineHeight: '1.5', paddingLeft: 1 }}>
        {params.value ? 'AUD' + params.value : '-'}
      </Box>
    ),
  },
  {
    field: 'margin_percent__c',
    headerName: 'Margin %',
    flex: 1,
    renderCell: (params) => (
      <Box sx={{ whiteSpace: 'normal', lineHeight: '1.5', paddingLeft: 1 }}>
        {params.value ? params.value + '%' : '-'}
      </Box>
    ),
  },
  {
    field: 'inventory',
    headerName: 'Inventory',
    align: 'center',
    headerAlign: 'center',
    type: 'actions',
    renderCell: (params) => (
      <Box display="flex" justifyContent="center">
        <Link
          to={
            '/vendors/' +
            params.row?.vendorId +
            '/properties/' +
            params.row?.fk_property_id +
            '/room-types/' +
            params.row?.fk_room_type_id +
            '/room-rates/' +
            params.row?.fk_room_rate_id
          }
          target="_blank"
        >
          <IconButton>
            <Inventory2Outlined />
          </IconButton>
        </Link>
      </Box>
    ),
  },
];

interface Props {
  opportunityInfo: App.OpportunityInfo;
  additionalQuestions: App.AdditionalQuestions;
  contract: App.Contract;
  deals: Array<App.Deal>;
  isAdmin: boolean;
  offerId: string;
  offerResponse: any;
  vendorId: string;
}

export default function OpportunityDealDetails({
  opportunityInfo,
  additionalQuestions,
  contract,
  deals,
  isAdmin,
  offerId,
  offerResponse,
  vendorId,
}: Props) {
  const [open, setOpen] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState<App.Deal | null>(null);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const dealsMappedToPackages = useMemo(() => {
    return deals.map((deal) => {
      const dealPackage = offerResponse.packages.find((pkg) => pkg?.id_salesforce_external === deal?.sfid);
      return {
        ...deal,
        fk_room_rate_id: dealPackage?.fk_room_rate_id,
        fk_room_type_id: dealPackage?.fk_room_type_id,
        fk_property_id: dealPackage?.fk_property_id,
        vendorId,
      };
    });
  }, [deals, offerResponse.packages, vendorId]);

  const openDealInclusionsModal = (row: GridRowParams<App.Deal>) => {
    if (row.row) {
      setSelectedDeal(row.row);
      handleOpen();
    }
  };

  return (
    <>
      {open && (
        <OpportunityDealInclusionsModal
          open={open}
          handleClose={handleClose}
          dealName={selectedDeal?.name}
          offerId={offerId}
          dealSalesforceId={selectedDeal?.sfid}
          currencyisocode={selectedDeal?.currencyisocode}
        />
      )}
      <Box id="deal-options" mt={2}>
        <Accordion title="Deal options" icon={<ListOutlined />} columns={1}>
          <Table
            columns={columns}
            rows={dealsMappedToPackages}
            getRowId={(row) => row.id}
            paginationMode="client"
            onRowClick={(row) => openDealInclusionsModal(row)}
            dataGridProps={{
              initialState: {
                pagination: {
                  paginationModel: { page: 0, pageSize: 10 },
                },
              },
              autoHeight: true,
              getRowHeight: () => 'auto',
              sx: {
                '& .MuiDataGrid-cell': {
                  padding: '6px',
                  whiteSpace: 'normal',
                  lineHeight: '1.5',
                  alignItems: 'flex-start',
                },
              },
            }}
          />
        </Accordion>
      </Box>

      {isAdmin && (
        <Box id="additional-questions" mt={2}>
          <Accordion title="Additional Questions" icon={<ContactSupportOutlined />}>
            <ReadOnlyField
              title="Educational Package Status"
              value={additionalQuestions?.educational_package_status__c || '-'}
              type="text"
            />
            <ReadOnlyField
              title="Educational Package Wording"
              value={additionalQuestions?.educational_packages_wording__c || '-'}
              type="text"
            />
            <ReadOnlyField
              title="Educational Package"
              value={additionalQuestions?.educational_package__c || '-'}
              type="text"
            />
            <ReadOnlyField
              title="Educational Package Used"
              value={additionalQuestions?.educational_package_used__c || '-'}
              type="text"
            />
            <ReadOnlyField
              title="Educational Package Comments"
              value={additionalQuestions?.educational_package_comments__c || '-'}
              type="text"
            />
          </Accordion>
        </Box>
      )}

      <Box id="payments" mt={2}>
        <Accordion title="Payments" icon={<ReceiptLongOutlined />}>
          <ReadOnlyField title="Payment method" value={contract?.payment_method__c || '-'} type="text" />
          <ReadOnlyField title="Payment milestone" value={contract?.payment_milestone__c || '-'} type="text" />
          <ReadOnlyField
            title="Cost Price Currency"
            value={opportunityInfo?.cost_price_currency__c || '-'}
            type="text"
          />
        </Accordion>
      </Box>
    </>
  );
}
